import React, { FC, useMemo } from "react";
import { additionalServicesIcons } from "@/utils/additional";
import { useAllShipmentServicesRequest } from "@/api/useShipmentServicesRequest";
import { AddIcon } from "@/components/assets/icons";
import { useTranslations } from "next-intl";
import { ModalType } from "@/enums";
import { Theme } from "@/enums";
import { useParcels } from "@/context/Parcels.context";
import { useTheme } from "@/context/Theme.context";
import { ICurrentShipment } from "@/types/apiResponse.types";
import classNames from "classnames";
import { parcelServiceEditableStatuses } from "../parcel-form-components/parcel-services";
import { formatNumber } from "@/utils/formatNumber";

type TProps = {
  currentParcel: ICurrentShipment;
  parcelId: number;
};

const ViewParcelServices: FC<TProps> = ({ currentParcel, parcelId }) => {
  const t = useTranslations();

  const { openParcelsModal, setActiveStep } = useParcels();
  const theme = useTheme();

  const { data: servicesData } = useAllShipmentServicesRequest();
  const shipmentServices = servicesData?.data?.data || [];

  const additionalServices = useMemo(() => {
    return shipmentServices.filter((service) =>
      currentParcel?.shipmentServiceIds?.includes(service.serviceId),
    );
  }, [shipmentServices, currentParcel?.shipmentServiceIds]);

  const openAdditionalServices = () => {
    openParcelsModal({ id: parcelId, modalType: ModalType.EDIT });
    if (theme === Theme.UZ) {
      setActiveStep(2);
    } else if (theme === Theme.UK) {
      setActiveStep(2);
    } else {
      setActiveStep(3);
    }
  };

  const isServicesEditable = parcelServiceEditableStatuses.includes(
    currentParcel?.commonStatus,
  );

  return (
    <>
      <div className="mt-7">
        <div className="flex w-full items-center justify-between">
          <h2 className="text-sm font-medium uppercase text-[#b2b2b2]">
            {t("additional_services")}
          </h2>
          {isServicesEditable && (
            <div
              className="primary-bg flex h-8 w-8 cursor-pointer items-center justify-center rounded-full p-1"
              onClick={openAdditionalServices}
            >
              <AddIcon className="h-5 w-5 fill-white" />
            </div>
          )}
        </div>
        <div className="mt-7 flex flex-col gap-[10px]">
          {additionalServices.map((service) => {
            const Icon =
              additionalServicesIcons.find(
                (icon) => icon.serviceId === service.serviceId,
              )?.icon || null;

            const servicePrice =
              theme === Theme.UZ
                ? service.servicePriceNational
                : service.servicePrice;

            return (
              <div className="flex gap-[10px]" key={service.serviceId}>
                <div className="secondary-bg flex h-[2.87rem] w-[2.87rem] items-center justify-center rounded-full p-2">
                  {Icon && (
                    <Icon
                      className={classNames({
                        "fill-[#EB7623]": theme === Theme.UK,
                        "fill-[#13CFE8]": theme !== Theme.UK,
                      })}
                    />
                  )}
                </div>
                <div className="flex flex-col">
                  <h2 className="text-sm font-semibold text-black">
                    {service.serviceName}
                  </h2>
                  <p className="text-sm font-normal text-black">
                    {service.serviceId === 113
                      ? theme === Theme.GEORGIA
                        ? `${service.baseCostForServiceInsuranceInUsd || 0}$`
                        : `${formatNumber(servicePrice || 0)}%`
                      : `${theme === Theme.UZ ? "UZS " : "$ "} ${formatNumber(servicePrice || 0)}`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default ViewParcelServices;
